(() => {
  const cache = new Map();
  const productCache = new Map();
  let liveBundleProductHandles = new Set();
  const normalizeHandles = handles => new Set((Array.isArray(handles) ? handles : [])
    .filter(handle => typeof handle === 'string' && handle.trim()).map(handle => handle.trim()));
  const sameSet = (left, right) => left.size === right.size && [...left].every(value => right.has(value));
  const publishBundleProductHandles = handles => {
    const next = normalizeHandles(handles);
    if (sameSet(liveBundleProductHandles, next)) return;
    liveBundleProductHandles = next;
    window.dispatchEvent(new CustomEvent('bt:bundle-products-updated', { detail: { handles: [...next] } }));
  };
  const readPlan = element => {
    try { return JSON.parse(element.querySelector('[data-bt-related-plan]').textContent); } catch { return null; }
  };
  const readCandidates = root => [...root.querySelectorAll('[data-bt-related-candidate]')].flatMap(element => {
    try {
      const candidate = JSON.parse(element.textContent);
      const template = [...root.querySelectorAll('template[data-bt-related-card]')].find(card => card.dataset.handle === candidate.handle);
      return [{ ...candidate, template, manual: template?.dataset.manual === 'true' }];
    } catch { return []; }
  });
  const selectionFor = plan => {
    const selection = window.BtProductSelectionContext?.get?.();
    const price = value => value === null || (Number.isSafeInteger(value) && value >= 0);
    return selection?.currency === plan.currency && selection?.country === plan.country &&
      Array.isArray(selection.ages) && selection.ages.every(age => typeof age === 'string') &&
      (selection.vendors === undefined || (Array.isArray(selection.vendors) &&
        selection.vendors.every(vendor => typeof vendor === 'string' && vendor.trim()))) &&
      price(selection.minPrice) && price(selection.maxPrice) &&
      !(selection.minPrice !== null && selection.maxPrice !== null && selection.minPrice > selection.maxPrice) ? selection : null;
  };
  const safePrices = candidate => Array.isArray(candidate.prices) && candidate.prices.length &&
    candidate.prices.every(price => Number.isSafeInteger(price) && price > 0);
  const identities = (product, tier) => (product.collections || []).filter(source => source.tier === tier).map(source => source.handle);
  const overlap = (left, right) => left.some(value => right.includes(value));
  const automaticOrder = anchor => {
    const anchorPrice = Math.min(...anchor.prices);
    const sameAge = candidate => Number.isFinite(anchor.months) && candidate.months === anchor.months;
    return (a, b) => Number(sameAge(b)) - Number(sameAge(a)) ||
      Math.abs(Math.log(Math.min(...a.prices) / anchorPrice)) -
      Math.abs(Math.log(Math.min(...b.prices) / anchorPrice)) || a.handle.localeCompare(b.handle);
  };
  const eligible = (candidate, plan, source, manual = false, selection = null) => {
    const anchor = plan.anchor;
    if (!candidate.handle || candidate.id === anchor.id || candidate.giftCard || !safePrices(candidate)) return false;
    if (Number.isFinite(anchor.months) && Number.isFinite(candidate.months) && candidate.months > anchor.months) return false;
    if (!manual) {
      if (source?.nativeRelated !== true) {
        if (!source || !candidate.collections?.some(collection => collection.handle === source.handle)) return false;
        const sameAgeOnly = source.sameAgeOnly === true && source.tier >= 3;
        if (source.nativeOnly === true) {
          // Missing identity permits only Shopify-ranked, exact-age category matches.
          if (source.nativeFallback !== true || anchor.vendor || !Number.isFinite(anchor.months) ||
              candidate.months !== anchor.months) return false;
        } else if (source.tier >= 3 && !sameAgeOnly) {
          const sameVendor = anchor.vendor && candidate.vendor === anchor.vendor;
          const sameManufacturer = anchor.manufacturer && candidate.manufacturer === anchor.manufacturer;
          if (!sameVendor && !sameManufacturer) return false;
        }
        if (sameAgeOnly && (!Number.isFinite(anchor.months) || candidate.months !== anchor.months)) return false;
        const universes = identities(anchor, 1), candidateUniverses = identities(candidate, 1);
        if (universes.length && candidateUniverses.length && !overlap(universes, candidateUniverses)) return false;
        if (source.tier >= 3) {
          const anchorIdentities = [...universes, ...identities(anchor, 2)];
          const candidateIdentities = [...candidateUniverses, ...identities(candidate, 2)];
          if (anchorIdentities.length && candidateIdentities.length && !overlap(anchorIdentities, candidateIdentities)) return false;
        }
      }
    }
    // Curation determines affinity and order; explicit shopper choices still apply.
    // The catalog brand is distinct from the manufacturer used for affinity.
    if (selection?.vendors?.length && !selection.vendors.includes(candidate.catalogVendor)) return false;
    if (selection?.ages.length && !selection.ages.includes(candidate.age)) return false;
    if (selection && !candidate.prices.some(price => (selection.minPrice === null || price >= selection.minPrice) &&
        (selection.maxPrice === null || price <= selection.maxPrice))) return false;
    return true;
  };
  const request = async url => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const path = url.pathname + url.search;
      const response = await fetch(typeof cartSectionUrl === 'function' ? cartSectionUrl(path) : path,
        { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product suggestion request failed');
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } finally { clearTimeout(timer); }
  };
  class BrincatoysProductSuggestions extends HTMLElement {
    static sources(plan) {
      return [...new Map((plan.sources || []).map(source => [source.handle, source])).values()]
        .sort((a, b) => a.tier - b.tier || a.size - b.size);
    }
    static eligible = eligible;
    static async resolve(plan, seeds, loadPage, selection = null, loadRelated = null, onProgress = null) {
      const limit = Math.max(2, Math.min(10, Number(plan.limit) || 10));
      const complementaryIds = new Set((plan.complementaryIds || []).map(String));
      const bundleProductHandles = new Set((plan.bundleProductHandles || []).map(String));
      const isComplementary = candidate => complementaryIds.has(String(candidate.id)) ||
        bundleProductHandles.has(String(candidate.handle));
      const candidates = seeds.filter(candidate => !isComplementary(candidate) &&
        eligible(candidate, plan, null, true, selection)).slice(0, limit);
      const handles = new Set(candidates.map(candidate => candidate.handle));
      let failures = 0;
      let matchingTier = null;
      let hasNativeRelated = false;
      let nativeMatchingTier = null;
      let collectionRequests = 0;
      const publish = items => onProgress?.(items.slice(0, limit));
      publish(candidates);
      const nativeRelatedSource = { handle: 'shopify-related', tier: 0, nativeRelated: true };
      const nativeRelatedEligibilitySource = this.sources(plan).find(source =>
        source.tier >= 3 && source.sameAgeOnly === true) || nativeRelatedSource;
      if (!plan.manualOnly && candidates.length < limit && plan.recommendationsUrl && typeof loadRelated === 'function') {
        try {
          const page = await loadRelated(plan);
          if (page.source !== nativeRelatedSource.handle || page.currency !== plan.currency ||
              page.country !== plan.country || page.language !== plan.language) {
            throw new Error('Changed product recommendation context');
          }
          const sources = this.sources(plan);
          const matches = page.candidates.filter(candidate => {
            if (isComplementary(candidate) || handles.has(candidate.handle) ||
                !eligible(candidate, plan, nativeRelatedEligibilitySource, false, selection)) return false;
            const matchingSources = sources.filter(source => eligible(candidate, plan,
              source.nativeOnly ? { ...source, nativeFallback: true } : source, false, selection));
            if (!matchingSources.length) return false;
            const tier = Math.min(...matchingSources.map(source => source.tier));
            nativeMatchingTier = nativeMatchingTier === null ? tier : Math.min(nativeMatchingTier, tier);
            return true;
          });
          for (const candidate of matches) {
            if (candidates.length >= limit) break;
            handles.add(candidate.handle);
            candidates.push({ ...candidate, manual: false });
          }
          hasNativeRelated = matches.length > 0;
          publish(candidates);
        } catch { failures++; }
      }
      // A universe's best sellers can fill every slot before a matching toy format appears.
      // Inspect one filtered category page first; never broaden the viewed universe.
      const universes = identities(plan.anchor, 1);
      const formatSource = universes.length && plan.anchor.catalogVendor && plan.anchor.vendor &&
        this.sources(plan).find(source => source.tier === 3 &&
          identities(plan.anchor, 3).includes(source.handle));
      if (!plan.manualOnly && !hasNativeRelated && formatSource && candidates.length < limit) {
        const url = new URL(formatSource.url, location.origin);
        url.searchParams.set('filter.p.vendor', plan.anchor.catalogVendor);
        try {
          collectionRequests++;
          const page = await loadPage(url.pathname + url.search, formatSource, plan);
          if (page.collection !== formatSource.handle || page.currency !== plan.currency ||
              page.country !== plan.country || page.language !== plan.language || page.sort !== 'best-selling') {
            throw new Error('Changed product suggestion context');
          }
          const matches = page.candidates.filter(candidate => !isComplementary(candidate) && !handles.has(candidate.handle) &&
            overlap(universes, identities(candidate, 1)) && eligible(candidate, plan, formatSource, false, selection));
          matches.sort(automaticOrder(plan.anchor));
          for (const candidate of matches) {
            if (candidates.length >= limit || handles.has(candidate.handle)) continue;
            handles.add(candidate.handle);
            candidates.push({ ...candidate, manual: false });
          }
          publish(candidates);
        } catch { failures++; }
      }
      if (!plan.manualOnly) for (const source of this.sources(plan)) {
        if (source.nativeOnly) continue;
        // Complete a short native selection at its strongest affinity, never a weaker one.
        if (hasNativeRelated && source.tier !== nativeMatchingTier &&
            !(source.tier >= 3 && source.sameAgeOnly === true)) continue;
        if (candidates.length >= limit || collectionRequests >= 5 ||
            (matchingTier !== null && source.tier > matchingTier)) break;
        const visited = new Set();
        const matches = [];
        const preferSameAge = source.tier >= 3 && !source.sameAgeOnly &&
          Number.isFinite(plan.anchor.months) && plan.anchor.catalogType;
        const passes = preferSameAge ? [{ ...source, exactAge: true }, source] : [source];
        // Bound catalogue scans; publish each validated page while looking for more.
        for (const requestSource of passes) {
          let next = source.url;
          while (next && candidates.length + matches.length < limit &&
              !visited.has(JSON.stringify([next, !!requestSource.exactAge])) &&
              visited.size < 3 && collectionRequests < 5) {
            visited.add(JSON.stringify([next, !!requestSource.exactAge]));
            try {
              collectionRequests++;
              const page = await loadPage(next, requestSource, plan);
              if (page.collection !== source.handle || page.currency !== plan.currency || page.country !== plan.country || page.language !== plan.language || page.sort !== 'best-selling') {
                throw new Error('Changed product suggestion context');
              }
              for (const candidate of page.candidates) {
                if (isComplementary(candidate) || handles.has(candidate.handle) ||
                    !eligible(candidate, plan, source, false, selection)) continue;
                handles.add(candidate.handle);
                matches.push({ ...candidate, manual: false });
              }
              publish([...candidates, ...matches.slice().sort(automaticOrder(plan.anchor))]);
              next = page.next;
            } catch {
              failures++;
              break;
            }
          }
        }
        matches.sort(automaticOrder(plan.anchor));
        if (matches.length) matchingTier = source.tier;
        candidates.push(...matches.slice(0, limit - candidates.length));
      }
      return { candidates, failures };
    }
    connectedCallback() {
      this.retryButton = this.querySelector('[data-bt-related-retry]');
      this.bundleProductHandles = normalizeHandles(liveBundleProductHandles);
      this.onBundleProductsUpdated = event => {
        const handles = normalizeHandles(event.detail?.handles);
        if (sameSet(this.bundleProductHandles, handles)) return;
        this.bundleProductHandles = handles;
        if (this.pending) this.reloadAfterPending = true;
        else if (this.hasLoaded) this.load();
      };
      window.addEventListener('bt:bundle-products-updated', this.onBundleProductsUpdated);
      this.onRetry = () => { if (['error', 'partial'].includes(this.dataset.state)) this.load(); };
      this.retryButton?.addEventListener('click', this.onRetry);
      this.onReturn = () => { if (['error', 'partial'].includes(this.dataset.state)) this.load(); };
      window.addEventListener('pageshow', this.onReturn);
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '1200px' });
      this.observer.observe(this);
      this.onReady = () => {
        if (this.hasLoaded || this.pending) return;
        const start = () => { if (this.isConnected) this.load(); };
        if (typeof window.requestIdleCallback === 'function') {
          this.idleRequest = window.requestIdleCallback(start, { timeout: 1500 });
        } else {
          this.loadTimer = window.setTimeout(start, 400);
        }
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      else this.onReady();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.cancelScheduledLoad();
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.retryButton?.removeEventListener('click', this.onRetry);
      window.removeEventListener('bt:bundle-products-updated', this.onBundleProductsUpdated);
      window.removeEventListener('pageshow', this.onReturn);
    }
    cancelScheduledLoad() {
      if (this.idleRequest !== undefined) window.cancelIdleCallback?.(this.idleRequest);
      window.clearTimeout(this.loadTimer);
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.idleRequest = undefined;
      this.loadTimer = undefined;
    }
    updateLoadingStatus() {
      const loading = this.querySelector('[data-bt-related-loading]');
      if (loading) loading.hidden = this.dataset.state !== 'loading' ||
        !!this.querySelector('.product-grid > li');
    }
    setState(state) {
      this.dataset.state = state;
      this.updateLoadingStatus();
      const recovery = this.querySelector('[data-bt-related-recovery]');
      // Keep the retry control in place while its request is pending.
      if (recovery && state !== 'loading') recovery.hidden = !['error', 'partial'].includes(state);
      if (this.retryButton) this.retryButton.disabled = state === 'loading';
    }
    async loadPage(path, source, plan) {
      const url = new URL(path, location.origin), base = new URL(source.url, location.origin);
      if (url.origin !== location.origin || url.pathname !== base.pathname || !url.pathname.includes('/collections/')) {
        throw new Error('Invalid product suggestion source');
      }
      url.searchParams.set('section_id', 'brincatoys-related-candidates');
      url.searchParams.set('sort_by', 'best-selling');
      url.searchParams.set('filter.v.availability', '1');
      if (source.tier >= 3 && !source.sameAgeOnly && !plan.anchor.manufacturer && plan.anchor.catalogVendor &&
          plan.anchor.catalogVendor.trim().toLowerCase() === plan.anchor.vendor) {
        url.searchParams.set('filter.p.vendor', plan.anchor.catalogVendor);
      }
      if ((source.sameAgeOnly || source.exactAge) && plan.anchor.catalogType) {
        url.searchParams.set('filter.p.product_type', plan.anchor.catalogType);
      }
      const root = (await request(url)).querySelector('[data-bt-related-page]');
      if (!root) throw new Error('Product suggestion section unavailable');
      return { collection: root.dataset.collection, currency: root.dataset.currency, country: root.dataset.country,
        language: root.dataset.language, sort: root.dataset.sort, next: root.dataset.next, candidates: readCandidates(root) };
    }
    async loadRelated(plan) {
      if (!plan.recommendationsUrl) throw new Error('Product recommendation URL unavailable');
      const url = new URL(plan.recommendationsUrl, location.origin);
      if (url.origin !== location.origin || !url.pathname.endsWith('/recommendations/products') ||
          url.searchParams.get('product_id') !== String(plan.anchor.id) ||
          url.searchParams.get('section_id') !== 'brincatoys-related-candidates' ||
          url.searchParams.get('intent') !== 'related') throw new Error('Invalid product recommendation source');
      const root = (await request(url)).querySelector('[data-bt-related-page]');
      if (!root || root.dataset.source !== 'shopify-related') throw new Error('Shopify recommendations unavailable');
      return { source: root.dataset.source, currency: root.dataset.currency, country: root.dataset.country,
        language: root.dataset.language, candidates: readCandidates(root) };
    }
    async load() {
      this.cancelScheduledLoad();
      this.observer?.disconnect();
      if (this.pending) return this.pending;
      const storedPlan = readPlan(this);
      if (!storedPlan) return;
      this.hasLoaded = true;
      const plan = { ...storedPlan, bundleProductHandles: [...this.bundleProductHandles] };
      const selection = selectionFor(plan);
      const key = JSON.stringify([plan, selection, window.Shopify?.theme?.id]);
      const cached = cache.get(key);
      const entry = cached && Date.now() - cached.at < 30000 ? cached :
        { at: Date.now(), listeners: new Set(), candidates: [] };
      cache.set(key, entry);
      while (cache.size > 12) cache.delete(cache.keys().next().value);
      const restoreFocus = this.retryButton && document.activeElement === this.retryButton;
      this.setState('loading');
      this.setAttribute('aria-busy', 'true');
      // Share candidates with the bundle without presenting an unfinished layout.
      const progress = candidates => { if (this.isConnected) this.currentCandidates = candidates; };
      entry.listeners.add(progress);
      progress(entry.candidates);
      if (!entry.pending) entry.pending = BrincatoysProductSuggestions.resolve(plan, readCandidates(this),
        this.loadPage.bind(this), selection, this.loadRelated.bind(this), candidates => {
          entry.candidates = candidates;
          entry.listeners.forEach(listener => listener(candidates));
        });
      this.pending = entry.pending.then(result => {
        if (result.failures) cache.delete(key);
        // Apply exclusions received during this request before the first presentation.
        if (!this.isConnected || this.reloadAfterPending) return;
        this.render(result.candidates);
        this.setState(result.failures ? (result.candidates.length ? 'partial' : 'error') : 'ready');
        if (restoreFocus && !result.failures &&
            (document.activeElement === this.retryButton || document.activeElement === document.body)) {
          this.querySelector('.product-grid a[href*="/products/"]')?.focus({ preventScroll: true });
        }
      }).catch(() => { cache.delete(key); if (this.isConnected) this.setState('error'); })
        .finally(() => {
          entry.listeners.delete(progress);
          this.pending = null;
          this.removeAttribute('aria-busy');
          if (this.reloadAfterPending) {
            this.reloadAfterPending = false;
            queueMicrotask(() => { if (this.isConnected) this.load(); });
          }
        });
      return this.pending;
    }
    render(candidates) {
      this.currentCandidates = candidates;
      const root = this.querySelector('product-recommendations');
      const list = root?.querySelector('.product-grid');
      if (!list) return;
      const signature = JSON.stringify(candidates.map(candidate =>
        [candidate.handle, candidate.age, candidate.prices, !!candidate.manual]));
      if (signature === this.renderedSignature) return;
      this.renderedSignature = signature;
      const existing = new Map([...list.children].map(card => [card.dataset.btRecommendationHandle, card]));
      const focused = document.activeElement;
      const added = [];
      const cards = candidates.flatMap((candidate, index) => {
        let card = existing.get(candidate.handle);
        if (card && card.dataset.btRecommendationPrices !== JSON.stringify(candidate.prices)) card = null;
        if (!card) {
          card = candidate.template?.content.firstElementChild?.cloneNode(true);
          if (card) added.push(card);
        }
        if (!card) return [];
        card.id = 'Slide-' + root.dataset.sectionId + '-' + (index + 1);
        card.dataset.btRecommendationHandle = candidate.handle;
        card.dataset.btRecommendationAge = candidate.age;
        card.dataset.btRecommendationPrices = JSON.stringify(candidate.prices);
        card.dataset.btRecommendationManual = String(candidate.manual);
        card.classList.toggle('slider__slide', list.classList.contains('slider'));
        return [card];
      });
      for (const card of [...list.children]) if (!cards.includes(card)) card.remove();
      cards.forEach((card, index) => { if (list.children[index] !== card) list.insertBefore(card, list.children[index] || null); });
      if (focused !== document.body && focused?.isConnected && document.activeElement === document.body) {
        focused.focus({ preventScroll: true });
      }
      this.updateLoadingStatus();
      const layout = root.querySelector('.related-products');
      layout.hidden = !cards.length;
      layout.classList.toggle('related-products--short-desktop', cards.length <= 5);
      layout.classList.toggle('related-products--short-mobile', cards.length <= 2);
      layout.classList.toggle('related-products--compact', cards.length > 0 && cards.length <= 2);
      layout.classList.toggle('related-products--single', cards.length === 1);
      root.classList.toggle('product-recommendations--loaded', !!cards.length);
      root.querySelectorAll('.slider-counter--total').forEach(counter => { counter.textContent = String(cards.length); });
      root.querySelectorAll('[data-num-slides]').forEach(track => {
        track.dataset.numSlides = String(cards.length); track.style.setProperty('--num-slides', cards.length);
      });
      window.loadTemplateContent?.(root);
      window.loadDesktopOnlyTemplates?.();
      window.stickersReinit?.();
      customElements.whenDefined('product-form').then(() => {
        if (this.isConnected) added.forEach(card => card.querySelectorAll('product-form').forEach(form => form.init?.()));
      });
      const slider = root.querySelector('slider-component');
      if (slider?.slider) slider.resetPages?.();
    }
  }
  class BrincatoysProductBundle extends HTMLElement {
    connectedCallback() {
      this.onReturn = () => { if (this.dataset.state === 'error') this.validate(); };
      window.addEventListener('pageshow', this.onReturn);
      this.observer = new MutationObserver(() => this.validate());
      this.observer.observe(this, { childList: true, subtree: true });
      // Give the app's own recommendations time to arrive before recovering an empty offer.
      this.noOfferTimer = window.setTimeout(() => {
        const plan = readPlan(this);
        if (plan && !this.productPaths(plan).length) this.recover(plan);
      }, 6000);
      this.validate();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      window.clearTimeout(this.noOfferTimer);
      window.removeEventListener('pageshow', this.onReturn);
    }
    productPaths(plan) {
      const links = [...this.querySelectorAll('[data-essential-upsell-element="product"] a[href*="/products/"]')];
      return [...new Set(links.map(link => new URL(link.href, location.origin).pathname))]
        .filter(path => path.split('/').pop() !== plan.anchor.handle);
    }
    async recover(plan) {
      if (this.recovering || this.recovered) return;
      const app = this.querySelector('essential-upsell-frequently-bought-together-app-block');
      const suggestions = document.querySelector('brincatoys-product-suggestions');
      const suggestionPlan = suggestions && readPlan(suggestions);
      if (!app || !suggestionPlan || suggestionPlan.anchor.id !== plan.anchor.id ||
          ['language', 'currency', 'country'].some(field => suggestionPlan[field] !== plan[field])) return;
      let data;
      try { data = JSON.parse(app.getAttribute('upsell-app-data')); } catch { return; }
      if (data?.v !== 2 || !Array.isArray(data.funnels)) return;
      const funnelId = app.querySelector('[data-essential-upsell-funnel-id]')?.getAttribute('data-essential-upsell-funnel-id');
      const automatic = data.funnels.filter(funnel =>
        funnel.placement?.type === 'PRODUCT_PAGE' && funnel.upsell?.productsType === 'RECOMMENDATIONS_API' &&
        data.c?.[funnel.content]?.offerType === 'FREQUENTLY_BOUGHT_TOGETHER');
      const funnel = funnelId ? automatic.find(funnel => funnel.id === funnelId) :
        (automatic.length === 1 ? automatic[0] : null);
      // A recovery must not reinterpret hand-picked offers or custom discount rules.
      if (!funnel || funnel.upsell.discountType === 'CUSTOM' || funnel.upsell.customDiscounts?.length) return;
      this.recovering = true;
      try {
        if ((suggestions.currentCandidates?.length || 0) < 2) await suggestions.load();
        await customElements.whenDefined(app.localName);
        if (!this.isConnected || this.dataset.state === 'ready' || !('upsellAppData' in app)) return;
        const cartHandles = new Set(JSON.parse(app.getAttribute('cart-items') || '[]').map(item => item.handle));
        const excludedIds = new Set((funnel.placement.isExcludeProducts ? funnel.placement.excludedProducts || [] : [])
          .map(product => String(product.shopifyId).split('/').pop()));
        const candidates = (suggestions.currentCandidates || []).filter(candidate =>
          !excludedIds.has(String(candidate.id)) &&
          !(funnel.upsell.isHideProductInCart !== false && cartHandles.has(candidate.handle))).slice(0, 2);
        if (!candidates.length) return;
        const replacement = { ...funnel, upsell: { ...funnel.upsell, productsType: 'SPECIFIC',
          specificProducts: candidates.map(candidate => ({
            shopifyId: 'gid://shopify/Product/' + candidate.id, shopifyHandle: candidate.handle,
          })) } };
        this.recovered = true;
        this.recoveredHandles = new Set(candidates.map(candidate => candidate.handle));
        this.dataset.recovery = 'related-products';
        this.dataset.state = 'loading';
        this.querySelector('[data-bt-bundle-content]').hidden = true;
        // The app's reactive property rebuilds its own products, totals and cart action.
        app.upsellAppData = JSON.stringify({ ...data,
          funnels: data.funnels.map(current => current === funnel ? replacement : current) });
      } catch {
        if (this.isConnected) this.dataset.state = 'error';
      } finally { this.recovering = false; }
    }
    async validate() {
      const plan = readPlan(this);
      if (!plan) return;
      const paths = this.productPaths(plan);
      if (!paths.length) { publishBundleProductHandles([]); return; }
      const key = paths.join('|');
      if (this.validating === key || (this.validated === key && this.dataset.state !== 'error')) return;
      this.validating = key;
      const content = this.querySelector('[data-bt-bundle-content]');
      content.hidden = true;
      try {
        const candidates = await Promise.all(paths.map(async path => {
          const productKey = [path, plan.language, plan.currency, plan.country, window.Shopify?.theme?.id].join(':');
          const cached = productCache.get(productKey);
          let pending = cached && Date.now() - cached.at < 30000 ? cached.pending : null;
          if (!pending) {
            const prefix = plan.anchor.url.split('/products/')[0];
            const url = new URL(prefix + '/products/' + path.split('/').pop(), location.origin);
            url.searchParams.set('section_id', 'brincatoys-related-candidates');
            pending = request(url).then(html => {
              const root = html.querySelector('[data-bt-related-product]');
              if (root?.dataset.currency !== plan.currency || root?.dataset.country !== plan.country || root?.dataset.language !== plan.language) throw new Error('Changed offer market');
              const candidate = readCandidates(root)[0];
              if (!candidate || candidate.handle !== path.split('/').pop()) throw new Error('Offer product unavailable');
              return candidate;
            }).catch(error => { productCache.delete(productKey); throw error; });
            productCache.set(productKey, { at: Date.now(), pending });
            while (productCache.size > 20) productCache.delete(productCache.keys().next().value);
          }
          return pending;
        }));
        if (!this.isConnected || this.validating !== key) return;
        const valid = candidates.every(candidate => {
          const manual = [...plan.manualIds, ...plan.complementaryIds].includes(candidate.id);
          const source = BrincatoysProductSuggestions.sources(plan).find(source =>
            candidate.collections.some(collection => collection.handle === source.handle));
          return eligible(candidate, plan, source?.nativeOnly && this.recoveredHandles?.has(candidate.handle) ?
            { ...source, nativeFallback: true } : source, manual);
        });
        content.hidden = !valid;
        this.dataset.state = valid ? 'ready' : 'unrelated';
        publishBundleProductHandles(valid ? candidates.map(candidate => candidate.handle) : []);
        this.validated = key;
        if (valid) window.clearTimeout(this.noOfferTimer);
        else this.recover(plan);
      } catch {
        publishBundleProductHandles([]);
        if (this.isConnected) this.dataset.state = 'error';
      }
      finally { if (this.validating === key) this.validating = null; }
    }
  }
  customElements.define('brincatoys-product-suggestions', BrincatoysProductSuggestions);
  customElements.define('brincatoys-product-bundle', BrincatoysProductBundle);
})();
